import React, { useState } from 'react';
import { useDrag } from 'react-dnd';
import { Card, CardContent } from '@/components/ui/card';
import { Edit, Trash2 } from 'lucide-react';

export interface RelativeBreak {
  startMinute: number;
  durationMinutes: number;
}

export interface ShiftTemplate {
  id: string;
  business_id: string;
  name: string;
  duration: number;
  breaks: RelativeBreak[]; // Use the new RelativeBreak type
  created_at: string | null;
}

interface ShiftTemplateItemProps {
  template: ShiftTemplate;
  compact?: boolean;
  onEdit: (templateId: string) => void;
  onDelete: (templateId: string) => void;
}

const INTERVAL_MINUTES = 15;
const PIXELS_PER_MINUTE = 25 / INTERVAL_MINUTES;

const ShiftTemplateItem: React.FC<ShiftTemplateItemProps> = ({
  template,
  compact = false,
  onEdit,
  onDelete,
}) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'SHIFT_TEMPLATE',
    item: { templateId: template.id, name: template.name, duration: template.duration, breaks: template.breaks }, // breaks is already ShiftBreak[]
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }), [template.id, template.breaks]);

  // Format duration in hours and minutes
  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours > 0 ? `${hours}h ` : ''}${mins > 0 ? `${mins}m` : ''}`;
  };

  // Calculate the width in pixels based on duration
  const templateWidthPixels = template.duration * PIXELS_PER_MINUTE;

  // Calculate scale factor to fit within 9rem (144px) max width
  const maxWidthPixels = 144 - 16; // 9rem minus padding
  const scaleToFit = templateWidthPixels > maxWidthPixels ? maxWidthPixels / templateWidthPixels : 1;
  const displayWidth = Math.min(templateWidthPixels, maxWidthPixels);

  return (
    <div
      ref={drag}
      style={{ opacity: isDragging ? 0.5 : 1, cursor: 'move', width: `${displayWidth}px`, position: 'relative' }}
      className="touch-none"
    >
      <Card className={`border border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-400 transition-colors ${compact ? 'p-1' : ''}`}>
        <CardContent className={compact ? "p-1 flex flex-col" : "p-2 flex flex-col"}>
          <div className="w-full min-h-0">
            <h4 className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate" title={template.name}>
              {template.name}
            </h4>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {formatDuration(template.duration)}
            </p>
          </div>

          {/* Visual representation of template with breaks */}
          <div className="w-full h-4 bg-blue-100 dark:bg-blue-900 rounded-sm mt-1 relative overflow-hidden">
            {/* Template duration bar */}
            <div className="absolute inset-0 bg-blue-200 dark:bg-blue-800 rounded-sm" />

            {/* Breaks overlay */}
            {template.breaks && template.breaks.length > 0 && template.breaks.map((breakItem, index) => {
              const breakStartPercent = (breakItem.startMinute / template.duration) * 100;
              const breakWidthPercent = (breakItem.durationMinutes / template.duration) * 100;
              return (
                <div
                  key={index}
                  className="absolute bg-gray-400 dark:bg-gray-600 rounded-sm"
                  style={{
                    left: `${breakStartPercent}%`,
                    width: `${breakWidthPercent}%`,
                    height: '100%',
                  }}
                  title={`Break: ${formatDuration(breakItem.durationMinutes)} starting at ${formatDuration(breakItem.startMinute)}`}
                />
              );
            })}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-center gap-1 mt-1">
            <Edit
              className="h-3 w-3 text-gray-500 dark:text-gray-400 cursor-pointer hover:text-blue-600 dark:hover:text-blue-300"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(template.id);
              }}
              title="Edit template"
            />
            <Trash2
              className="h-3 w-3 text-gray-500 dark:text-gray-400 cursor-pointer hover:text-red-600 dark:hover:text-red-300"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(template.id);
              }}
              title="Delete template"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ShiftTemplateItem;
